import { useEffect, useMemo, useState } from 'react';
import {
  faTableCellsHeader,
  faTableColumnsAddAfter,
  faTableColumnsAddBefore,
  faTableColumnsRemoveAfter,
  faTableRowsAddAbove,
  faTableRowsAddBelow,
  faTableRowsRemoveBelow,
  faTrashCan,
} from '@awesome.me/kit-4a6bf60416/icons/classic/regular';
import type { ChainedCommands, Editor } from '@tiptap/core';
import { useCurrentEditor, useEditorState } from '@tiptap/react';

import {
  canDeleteTableColumn,
  canDeleteTableRow,
  findTableDepth,
  isHeaderRowActive,
} from '@/components/editor/nodes/table';
import { Icon } from '@/components/ui/icon';
import { IconTooltipButton } from '@/components/ui/icon-tooltip-button';
import { Separator } from '@/components/ui/separator';
import { EditorToolbarSurface } from './editor-toolbar-surface';
import { FloatingBlockToolbarPortal } from './floating-block-toolbar-portal';

function getActiveTablePosition(editor: Editor): number | null {
  const tableDepth = findTableDepth(editor);
  return tableDepth < 0 ? null : editor.state.selection.$from.before(tableDepth);
}

function getTableAnchorElement(editor: Editor, position: number): HTMLElement | null {
  const dom = editor.view.nodeDOM(position);
  const HTMLElementConstructor = editor.view.dom.ownerDocument.defaultView?.HTMLElement;
  if (!HTMLElementConstructor || !(dom instanceof HTMLElementConstructor)) return null;

  const table = dom.matches('table') ? dom : dom.querySelector('table');
  if (!table) return null;

  const scrollWrapper = table.closest('.tableWrapper');
  return scrollWrapper instanceof HTMLElementConstructor ? scrollWrapper : table;
}

/** Contextual table actions positioned against the table containing the caret. */
export function TableContextMenu() {
  const { editor } = useCurrentEditor();
  const [engaged, setEngaged] = useState(false);
  const tableState = useEditorState({
    editor,
    selector: (ctx) => {
      if (!ctx.editor?.isActive('table')) return null;

      return {
        tablePosition: getActiveTablePosition(ctx.editor),
        hasHeaderRow: isHeaderRowActive(ctx.editor),
        canDeleteRow: canDeleteTableRow(ctx.editor),
        canDeleteColumn: canDeleteTableColumn(ctx.editor),
      };
    },
  });

  useEffect(() => {
    if (!editor) {
      setEngaged(false);
      return;
    }

    const doc = editor.view.dom.ownerDocument;
    const syncEngagement = () => {
      setEngaged(
        editor.isFocused || Boolean(doc.activeElement?.closest('[data-table-context-menu]')),
      );
    };
    const scheduleSync = () => queueMicrotask(syncEngagement);

    doc.addEventListener('focusin', scheduleSync);
    doc.addEventListener('focusout', scheduleSync);
    syncEngagement();

    return () => {
      doc.removeEventListener('focusin', scheduleSync);
      doc.removeEventListener('focusout', scheduleSync);
    };
  }, [editor]);

  const tableAnchorElement =
    editor && tableState?.tablePosition != null
      ? getTableAnchorElement(editor, tableState.tablePosition)
      : null;
  const tableAnchorRef = useMemo(() => ({ current: tableAnchorElement }), [tableAnchorElement]);

  if (!editor || !tableState || !tableAnchorElement || !engaged) return null;

  const runTableCommand = (command: (chain: ChainedCommands) => ChainedCommands) => {
    command(editor.chain().focus()).run();
  };

  const { hasHeaderRow, canDeleteRow, canDeleteColumn } = tableState;

  return (
    <FloatingBlockToolbarPortal
      anchorRef={tableAnchorRef}
      placement="below"
      clampToViewport={false}
    >
      <EditorToolbarSurface role="toolbar" aria-label="Table actions" data-table-context-menu>
        <IconTooltipButton
          tooltip="Add row above"
          size="icon"
          onClick={() => runTableCommand((chain) => chain.addRowBefore())}
        >
          <Icon icon={faTableRowsAddAbove} />
        </IconTooltipButton>
        <IconTooltipButton
          tooltip="Add row below"
          size="icon"
          onClick={() => runTableCommand((chain) => chain.addRowAfter())}
        >
          <Icon icon={faTableRowsAddBelow} />
        </IconTooltipButton>
        <IconTooltipButton
          tooltip="Delete row"
          size="icon"
          disabled={!canDeleteRow}
          onClick={() => runTableCommand((chain) => chain.deleteRow())}
        >
          <Icon icon={faTableRowsRemoveBelow} />
        </IconTooltipButton>

        <Separator orientation="vertical" />

        <IconTooltipButton
          tooltip="Add column left"
          size="icon"
          onClick={() => runTableCommand((chain) => chain.addColumnBefore())}
        >
          <Icon icon={faTableColumnsAddBefore} />
        </IconTooltipButton>
        <IconTooltipButton
          tooltip="Add column right"
          size="icon"
          onClick={() => runTableCommand((chain) => chain.addColumnAfter())}
        >
          <Icon icon={faTableColumnsAddAfter} />
        </IconTooltipButton>
        <IconTooltipButton
          tooltip="Delete column"
          size="icon"
          disabled={!canDeleteColumn}
          onClick={() => runTableCommand((chain) => chain.deleteColumn())}
        >
          <Icon icon={faTableColumnsRemoveAfter} />
        </IconTooltipButton>

        <Separator orientation="vertical" />

        <IconTooltipButton
          tooltip={hasHeaderRow ? 'Make first row regular' : 'Make first row a header'}
          size="icon"
          variant={hasHeaderRow ? 'solid' : 'ghost'}
          onClick={() => runTableCommand((chain) => chain.toggleHeaderRow())}
        >
          <Icon icon={faTableCellsHeader} />
        </IconTooltipButton>

        <Separator orientation="vertical" />

        <IconTooltipButton
          tooltip="Delete table"
          size="icon"
          colorScheme="destructive"
          onClick={() => runTableCommand((chain) => chain.deleteTable())}
        >
          <Icon icon={faTrashCan} />
        </IconTooltipButton>
      </EditorToolbarSurface>
    </FloatingBlockToolbarPortal>
  );
}
